<template>
  <HrPageLayout
    title="PWD Management"
    subtitle="Manage employees living with disabilities"
    icon="♿"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'PWD Management' }]"
  >
    <template #header-actions>
      <button @click="refreshData" class="hr-btn hr-btn-secondary">
        <i class="bx bx-refresh"></i> Refresh
      </button>
      <button @click="openRecordModal" class="hr-btn hr-btn-success">
        <i class="mdi mdi-plus"></i> Record PWD
      </button>
      <button @click="generateReport" class="hr-btn hr-btn-primary">
        <i class="mdi mdi-file-pdf"></i> Generate Report
      </button>
    </template>

    <div class="layout-content">
      <div class="page-wrapper">
        <div class="filters-card">
          <div class="filters-header">
            <h3 class="filters-title">Filter Employees</h3>
          </div>
          <div class="filters-grid">
            <div class="filter-group">
              <label class="filter-label">Department</label>
              <select v-model="filters.department" class="filter-select" @change="filterRecords">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.name }}
                </option>
              </select>
            </div>
            <div class="filter-group">
              <label class="filter-label">Search</label>
              <div class="filter-input-wrap">
                <i class="bx bx-search filter-icon"></i>
                <input type="text" v-model="filters.search" class="filter-input" placeholder="Search by Name or ID..." @input="filterRecords">
              </div>
            </div>
          </div>
        </div>

        <div class="table-wrap card-section mt-4">
          <table class="table table-bordered table-sm modern-table">
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>National ID</th>
                <th>Department</th>
                <th>Date Recorded</th>
                <th>Justification</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in paginatedRecords" :key="c.id" class="table-row-hover">
                <td>
                  <div class="fw-bold text-dark">{{ c.name || c.national_id }}</div>
                </td>
                <td>{{ c.national_id }}</td>
                <td>{{ c.department_name }}</td>
                <td>{{ c.pwd_date || '–' }}</td>
                <td>
                  <template v-if="c.pwd_documents">
                    <div class="d-flex gap-1 flex-wrap">
                      <a v-for="(doc, idx) in parseDocuments(c.pwd_documents)" :key="idx" :href="getAttachmentUrl(doc.url || doc)" target="_blank" class="action-icon view-doc" :title="doc.name || ('View Document ' + (idx + 1))">
                        <i class="mdi mdi-file-document-outline"></i>
                      </a>
                    </div>
                  </template>
                  <span v-else class="text-muted">–</span>
                </td>
                <td class="text-end">
                  <button type="button" class="btn-action edit" @click="openEditModal(c)" title="Edit Status">
                    <i class="mdi mdi-pencil"></i> Edit
                  </button>
                </td>
              </tr>
              <tr v-if="!paginatedRecords.length">
                <td colspan="6" class="empty-state">
                  <div class="empty-state-content">
                    <i class="mdi mdi-inbox-remove"></i>
                    <p>No records found matching your criteria</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="d-flex justify-content-between align-items-center mt-3" v-if="totalPages > 1">
            <span class="text-muted small">Showing page {{ currentPage }} of {{ totalPages }}</span>
            <div class="btn-group">
              <button class="btn btn-sm btn-outline-secondary" :disabled="currentPage === 1" @click="currentPage--">Prev</button>
              <button class="btn btn-sm btn-outline-secondary" :disabled="currentPage === totalPages" @click="currentPage++">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Record/Edit Modal -->
    <div class="modal-overlay" v-if="showPwdModal" @click.self="showPwdModal = false">
      <div class="modal-dialog-custom" style="max-width: 600px;">
        <div class="modal-content premium-modal">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="mdi mdi-wheelchair-accessibility text-success me-2"></i>
              {{ isEdit ? 'Update PWD Status' : 'Record PWD' }}
            </h5>
            <button type="button" class="custom-close" @click="showPwdModal = false">
              <i class="bx bx-x"></i>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitForm">
              <div class="mb-3" v-if="!isEdit">
                <label class="form-label">Search Employee (Name, ID)</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="employeeSearch" 
                  @input="searchEmployees"
                  placeholder="Type to search..." 
                  required 
                />
                <select class="form-select mt-2" v-model="form.employee_id" @change="onEmployeeSelect" v-if="employeeSearch && searchedEmployees.length > 0">
                  <option value="">Select Employee...</option>
                  <option v-for="emp in searchedEmployees" :key="emp.id" :value="emp.id">
                    {{ emp.displayName || emp.name }} - {{ emp.departmentName || emp.department_name || emp.department }} ({{ emp.empNo || emp.employeeNumber || emp.employee_number }} / {{ emp.nationalId || emp.nationalID || emp.national_id }})
                  </option>
                </select>
                <div v-if="employeeSearch && searchedEmployees.length === 0" class="text-danger mt-1 text-sm">
                  No employee found matching query.
                </div>
              </div>
              <div class="mb-3" v-else>
                <label class="form-label">Employee</label>
                <input type="text" class="form-control" :value="form.employee_name" disabled />
              </div>

              <div class="mb-3">
                <label class="form-label">PWD Status</label>
                <select class="form-select" v-model="form.pwd_status" required>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label d-flex justify-content-between align-items-center mb-1">
                  Justification Documents
                  <button type="button" class="btn btn-sm btn-outline-primary py-0 px-2" @click="addDocumentRow" v-if="!isEdit || form.pwd_status === 'Yes'">
                    <i class="bx bx-plus"></i> Add Document
                  </button>
                </label>
                
                <div v-for="(row, index) in form.documentRows" :key="index" class="d-flex gap-2 mb-2 align-items-center bg-light p-2 rounded border">
                  <input type="text" class="form-control form-control-sm w-50" v-model="row.name" placeholder="Name (e.g. Medical Report)" required>
                  <input type="file" class="form-control form-control-sm" @change="e => onDynamicDocumentUpload(e, index)" accept=".pdf,image/*" :required="!row.file && !isEdit">
                  <button type="button" class="btn btn-sm btn-outline-danger py-0 px-2" @click="removeDocumentRow(index)" title="Remove">
                    <i class="bx bx-x"></i>
                  </button>
                </div>
                
                <small class="text-muted" v-if="form.documentRows.length === 0">No documents selected.</small>
                <div class="text-muted text-sm mt-1" v-if="isEdit && form.pwd_documents">
                  <i class="mdi mdi-information-outline"></i> Uploading new files will add them to the existing documents.
                </div>
              </div>

              <div class="modal-footer px-0 pb-0 mt-4 border-0">
                <button type="button" class="hr-btn hr-btn-secondary" @click="showPwdModal = false">Cancel</button>
                <button type="submit" class="hr-btn hr-btn-success" :disabled="submitting">
                  <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Save Status
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from '@/components/hrActions/HrPageLayout.vue';
import { execute, authUrl } from '@/api';
import { downloadCountyTablePdf } from "@/utils/countyReportExport";

export default {
  name: 'PwdManagement',
  components: { HrPageLayout },
  data() {
    return {
      records: [],
      filteredRecords: [],
      departments: [],
      filters: { department: '', search: '' },
      showPwdModal: false,
      isEdit: false,
      submitting: false,
      employeeSearch: '',
      searchedEmployees: [],
      allEmployees: [],
      form: {
        employee_id: '',
        employee_name: '',
        pwd_status: 'Yes',
        documentRows: [],
        pwd_documents: ''
      },
      currentPage: 1,
      itemsPerPage: 10
    };
  },
  computed: {
    paginatedRecords() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredRecords.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredRecords.length / this.itemsPerPage) || 1;
    }
  },
  mounted() {
    this.fetchDepartments();
    this.fetchRecords();
    this.fetchAllEmployees();
  },
  methods: {
    refreshData() {
      this.fetchRecords();
    },
    fetchDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then(res => {
        if (res.data.success) {
          this.departments = res.data.data.departments || res.data.data;
        }
      }).catch(err => console.error(err));
    },
    fetchRecords() {
      const fd = new FormData();
      fd.append("function", "getPwdEmployees");
      execute(fd, authUrl).then(res => {
        if (res.data.success) {
          this.records = res.data.data || [];
          this.filterRecords();
        }
      }).catch(err => console.error(err));
    },
    fetchAllEmployees() {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      execute(fd, authUrl).then(res => {
        if (res.data.success) {
          this.allEmployees = res.data.data.employees || [];
        }
      }).catch(err => console.error(err));
    },
    filterRecords() {
      this.filteredRecords = this.records.filter(r => {
        const matchDept = !this.filters.department || r.departmentID === this.filters.department || r.department_id === this.filters.department;
        const search = this.filters.search.toLowerCase();
        const matchSearch = !search || 
          (r.name || '').toLowerCase().includes(search) || 
          (r.national_id || '').toLowerCase().includes(search);
        return matchDept && matchSearch;
      });
      this.currentPage = 1;
    },
    searchEmployees() {
      const query = this.employeeSearch.toLowerCase().trim();
      if (!query) {
        this.searchedEmployees = [];
        return;
      }
      this.searchedEmployees = this.allEmployees.filter(emp => {
        const fullName = `${emp.firstName || ''} ${emp.middleName || ''} ${emp.lastName || ''}`.toLowerCase();
        const fallbackName = (emp.name || emp.employeeName || '').toLowerCase();
        const name = fullName.trim() ? fullName : fallbackName;
        
        const idNum = (emp.nationalID || emp.nationalId || emp.national_id || emp.idNumber || '').toLowerCase();
        const empNum = (emp.empNo || emp.employeeNumber || emp.employee_number || '').toLowerCase();
        
        return name.includes(query) || idNum.includes(query) || empNum.includes(query);
      }).slice(0, 10).map(emp => ({
        ...emp,
        displayName: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name
      }));
    },
    onEmployeeSelect() {
      if (!this.form.employee_id) return;
      const emp = this.searchedEmployees.find(e => e.id === this.form.employee_id);
      if (emp) {
        this.form.employee_name = emp.displayName || emp.name;
      }
    },
    onDynamicDocumentUpload(e, index) {
      if (e.target.files && e.target.files[0]) {
        this.form.documentRows[index].file = e.target.files[0];
      } else {
        this.form.documentRows[index].file = null;
      }
    },
    addDocumentRow() {
      this.form.documentRows.push({ name: '', file: null });
    },
    removeDocumentRow(index) {
      this.form.documentRows.splice(index, 1);
    },
    parseDocuments(docsString) {
      if (!docsString) return [];
      try {
        const parsed = JSON.parse(docsString);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // Fallback for old comma-separated
        return docsString.split(',').map(url => ({ name: 'Document', url }));
      }
      return [];
    },
    getAttachmentUrl(path) {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      const baseUrl = authUrl.replace(/authentication\/index\.php$/, '');
      return baseUrl + path;
    },
    openRecordModal() {
      this.isEdit = false;
      this.employeeSearch = '';
      this.searchedEmployees = [];
      this.form = { employee_id: '', employee_name: '', pwd_status: 'Yes', documentRows: [{ name: '', file: null }], pwd_documents: '' };
      this.showPwdModal = true;
    },
    openEditModal(record) {
      this.isEdit = true;
      this.form = {
        employee_id: record.id,
        employee_name: record.name,
        pwd_status: record.pwd_status || 'Yes',
        documentRows: [],
        pwd_documents: record.pwd_documents
      };
      this.showPwdModal = true;
    },
    submitForm() {
      if (!this.form.employee_id) return alert("Please select an employee");
      this.submitting = true;
      const fd = new FormData();
      fd.append("function", "recordPwdStatus");
      fd.append("employee_id", this.form.employee_id);
      fd.append("pwd_status", this.form.pwd_status);
      
      let fileIndex = 0;
      if (this.form.documentRows && this.form.documentRows.length > 0) {
        this.form.documentRows.forEach((row) => {
          if (row.file) {
            fd.append(`documents[${fileIndex}]`, row.file);
            fd.append(`document_names[${fileIndex}]`, row.name || 'Document');
            fileIndex++;
          }
        });
      }
      execute(fd, authUrl).then(res => {
        this.submitting = false;
        if (res.data.success) {
          this.showPwdModal = false;
          this.fetchRecords();
        } else {
          alert(res.data.message || "Failed to save record");
        }
      }).catch(err => {
        this.submitting = false;
        alert(err.message || "An error occurred");
      });
    },
    async generateReport() {
      let filterText = [];
      if (this.filters.department) filterText.push(`Department: ${this.filters.department}`);
      const periodLabel = filterText.length ? filterText.join(' | ') : 'All Time';
      
      const tableData = this.filteredRecords.map(r => [
        r.name || r.national_id,
        r.national_id || '-',
        r.department_name || '-',
        r.pwd_date || '-'
      ]);

      await downloadCountyTablePdf({
        title: "Employees Living with Disabilities Report",
        periodLabel: periodLabel,
        headers: ['Employee Name', 'National ID', 'Department', 'Date Recorded'],
        rows: tableData,
        filename: `PWD_Report_${new Date().toISOString().split('T')[0]}.pdf`,
        orientation: "portrait"
      });
    },
  }
}
</script>

<style scoped>
/* Filters */
.filters-card { background: #fff; border-radius: 12px; padding: 1.25rem; margin-bottom: 1.5rem; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); border: 1px solid rgba(0, 0, 0, 0.05); }
.filters-header { margin-bottom: 1rem; border-bottom: 1px solid #e2e8f0; padding-bottom: 0.75rem; }
.filters-title { font-size: 1rem; font-weight: 600; color: #1e293b; margin: 0; }
.filters-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 1rem; }
.filter-group { display: flex; flex-direction: column; gap: 0.375rem; }
.filter-label { font-size: 0.813rem; font-weight: 600; color: #475569; }
.filter-select, .filter-input { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.875rem; color: #1e293b; transition: all 0.2s; }
.filter-select:focus, .filter-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
.filter-input-wrap { position: relative; }
.filter-icon { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.filter-input-wrap .filter-input { padding-left: 2.25rem; }

/* Modern Table */
.card-section { background: #fff; border-radius: 12px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); border: 1px solid rgba(0, 0, 0, 0.05); overflow: hidden; padding: 0; }
.table-wrap { overflow-x: auto; }
.modern-table { width: 100%; margin: 0; border-collapse: separate; border-spacing: 0; }
.modern-table th { background: #f8fafc; color: #475569; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; padding: 1rem; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
.modern-table td { padding: 1rem; border-bottom: 1px solid #e2e8f0; vertical-align: middle; font-size: 0.875rem; }
.table-row-hover:hover { background: #f8fafc; }
.fw-bold { font-weight: 600; }

/* Actions */
.btn-action { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.375rem 0.75rem; border-radius: 6px; font-size: 0.75rem; font-weight: 500; border: none; cursor: pointer; transition: all 0.2s; }
.btn-action.edit { background: #e0f2fe; color: #0284c7; }
.btn-action.edit:hover { background: #bae6fd; }
.action-icon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; color: #475569; background: #f1f5f9; transition: all 0.2s; font-size: 1.1rem; }
.action-icon.view-doc:hover { background: #e0e7ff; color: #4f46e5; }

/* Empty State */
.empty-state { padding: 3rem 1rem !important; text-align: center; }
.empty-state-content { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; color: #94a3b8; }
.empty-state-content i { font-size: 3rem; opacity: 0.5; }

/* Modal overlays */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1.5rem;
  overflow-y: auto;
}

.modal-dialog-custom {
  width: 100%;
  margin: auto;
  animation: modalFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: translateY(20px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.premium-modal {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.premium-modal .modal-header {
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.premium-modal .modal-title {
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
}

.custom-close {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.custom-close:hover {
  background: #e2e8f0;
  color: #0f172a;
  transform: rotate(90deg);
}

.premium-modal .modal-body {
  padding: 1.75rem;
  overflow-y: auto;
  max-height: calc(100vh - 220px);
  background: #f8fafc;
}

.form-label { font-size: 0.875rem; color: #334155; font-weight: 600; margin-bottom: 0.375rem; }
.form-control, .form-select { border-radius: 8px; border-color: #cbd5e1; padding: 0.6rem 0.75rem; transition: all 0.2s; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
.form-control:focus, .form-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
</style>
